import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  goalApi,
  Goal,
  GoalContribution,
  CreateGoalData,
  ContributeData,
  AdjustBalanceData,
} from '../../services/goalApi';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { useAuth } from '../../contexts/AuthContext';
import { canModifyTransaction } from '../../utils/contextPermissions';
import { canPayBills } from '../../utils/bills';
import { tokens } from '../../theme/tokens';
import { GoalFormDialog } from './GoalFormDialog';
import { ContributeDialog } from './ContributeDialog';
import { AdjustBalanceDialog } from './AdjustBalanceDialog';

const errorText = (err: any): string => err?.response?.data?.message || err?.message || String(err);

interface Options {
  /** a goal stopped being active (archived or deleted) */
  onGoalGone?: (goal: Goal) => void;
  /** a goal was just created */
  onCreated?: (goal: Goal) => void;
}

/**
 * Everything the goals of the current view need, shared by the Planning card
 * and a goal's own page: the goals and their trails, who may do what, the
 * actions, and the dialogs and menu those actions open (render `dialogs`
 * once, anywhere in the page).
 */
export const useGoalsSection = ({ onGoalGone, onCreated }: Options = {}) => {
  const { t } = useTranslation('planning');
  const { selectedContextId, selectedContext } = useFinancialContexts();
  const { state: authState } = useAuth();

  const [goals, setGoals] = useState<Goal[]>([]);
  // Every active goal's trail loads with the goals: the list's statuses and
  // the month's total need them before anything is opened.
  const [trails, setTrails] = useState<Record<string, GoalContribution[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [contributingGoal, setContributingGoal] = useState<Goal | null>(null);
  const [adjustingGoal, setAdjustingGoal] = useState<Goal | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; goal: Goal } | null>(null);
  const [deletingGoal, setDeletingGoal] = useState<Goal | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const contextRole = selectedContext?.memberRole;
  const currentUserId = authState.user?.id;
  const defaultCurrency = selectedContext?.defaultCurrency || authState.user?.defaultCurrency || 'BRL';
  const inGroup = Boolean(selectedContext && selectedContext.type !== 'personal');
  const canEdit = canPayBills(contextRole);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await goalApi.list({ contextId: selectedContextId, status: 'all' });
      const entries = await Promise.all(
        list
          .filter((goal) => goal.status === 'active')
          .map((goal) =>
            goalApi
              .listContributions(goal.id)
              .then((trail) => [goal.id, trail] as const)
              .catch(() => [goal.id, []] as const),
          ),
      );
      setGoals(list);
      setTrails(Object.fromEntries(entries));
    } catch (err: any) {
      setError(errorText(err));
    } finally {
      setIsLoading(false);
    }
  }, [selectedContextId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeGoals = useMemo(() => goals.filter((goal) => goal.status === 'active'), [goals]);
  const archivedGoals = useMemo(() => goals.filter((goal) => goal.status === 'archived'), [goals]);
  // One "now" per load, so the charts' memoized numbers do not churn on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [goals, trails]);

  const fail = (err: any) => setError(errorText(err));
  const canModify = (goal: Goal) => canModifyTransaction({ transactionUserId: goal.userId, currentUserId, contextRole });
  const canUndo = (entry: GoalContribution) =>
    entry.userId === currentUserId || contextRole === 'owner' || contextRole === 'admin';

  const createGoal = async (data: CreateGoalData) => {
    const created = await goalApi.create({ ...data, contextId: selectedContextId });
    await load();
    if (created?.id) onCreated?.(created);
  };

  const updateGoal = async (data: CreateGoalData) => {
    if (!editingGoal) return;
    const { contextId: _ignored, ...changes } = data;
    await goalApi.update(editingGoal.id, changes);
    await load();
  };

  const contribute = async (data: ContributeData) => {
    if (!contributingGoal) return;
    const result = await goalApi.contribute(contributingGoal.id, data);
    setMessage(result.goal.isAchieved ? (t('goals.achievedMessage') as string) : (t('goals.saved') as string));
    await load();
  };

  const adjustBalance = async (data: AdjustBalanceData) => {
    if (!adjustingGoal) return;
    const result = await goalApi.adjust(adjustingGoal.id, data);
    setMessage(result.goal.isAchieved ? (t('goals.achievedMessage') as string) : (t('goals.adjusted') as string));
    await load();
  };

  const archiveGoal = async (goal: Goal) => {
    setMenuAnchor(null);
    try {
      await goalApi.update(goal.id, { status: 'archived' });
      onGoalGone?.(goal);
      await load();
    } catch (err: any) {
      fail(err);
    }
  };

  const unarchiveGoal = async (goal: Goal) => {
    setMenuAnchor(null);
    try {
      await goalApi.update(goal.id, { status: 'active' });
      setMessage(t('goals.unarchived') as string);
      await load();
    } catch (err: any) {
      fail(err);
    }
  };

  const deleteGoal = async () => {
    const goal = deletingGoal;
    setDeletingGoal(null);
    if (!goal) return;
    try {
      await goalApi.remove(goal.id);
      onGoalGone?.(goal);
      await load();
    } catch (err: any) {
      fail(err);
    }
  };

  const removeContribution = async (goal: Goal, entry: GoalContribution) => {
    try {
      await goalApi.removeContribution(goal.id, entry.id);
      await load();
    } catch (err: any) {
      fail(err);
    }
  };

  const reorder = async (goalIds: string[]) => {
    // Optimistic: the list follows the finger; the server call makes it stick.
    const byId = new Map(activeGoals.map((goal) => [goal.id, goal]));
    setGoals([...goalIds.map((id) => byId.get(id)).filter((goal): goal is Goal => Boolean(goal)), ...archivedGoals]);
    try {
      await goalApi.reorder(goalIds);
    } catch (err: any) {
      fail(err);
      await load();
    }
  };

  const openCreate = () => {
    setEditingGoal(null);
    setFormOpen(true);
  };

  const dialogs = (
    <>
      <Menu anchorEl={menuAnchor?.element} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {menuAnchor?.goal.status === 'archived' ? (
          <MenuItem onClick={() => menuAnchor && unarchiveGoal(menuAnchor.goal)}>{t('goals.menu.unarchive')}</MenuItem>
        ) : (
          [
            <MenuItem
              key='edit'
              onClick={() => {
                if (menuAnchor) {
                  setEditingGoal(menuAnchor.goal);
                  setFormOpen(true);
                }
                setMenuAnchor(null);
              }}
            >
              {t('goals.menu.edit')}
            </MenuItem>,
            <MenuItem key='archive' onClick={() => menuAnchor && archiveGoal(menuAnchor.goal)}>
              {t('goals.menu.archive')}
            </MenuItem>,
          ]
        )}
        <MenuItem
          onClick={() => {
            if (menuAnchor) setDeletingGoal(menuAnchor.goal);
            setMenuAnchor(null);
          }}
          sx={{ color: tokens.danger }}
        >
          {t('goals.menu.delete')}
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(deletingGoal)} onClose={() => setDeletingGoal(null)} maxWidth='xs' fullWidth aria-labelledby='goal-delete-title'>
        <DialogTitle id='goal-delete-title'>{t('goals.delete.title', { name: deletingGoal?.name ?? '' })}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14.5, color: tokens.ink2 }}>{t('goals.delete.body')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeletingGoal(null)} color='inherit'>
            {t('goals.delete.cancel')}
          </Button>
          <Button onClick={deleteGoal} variant='contained' color='error'>
            {t('goals.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <GoalFormDialog
        open={formOpen}
        goal={editingGoal}
        defaultCurrency={defaultCurrency}
        onClose={() => {
          setFormOpen(false);
          setEditingGoal(null);
        }}
        onSubmit={editingGoal ? updateGoal : createGoal}
      />

      <ContributeDialog goal={contributingGoal} onClose={() => setContributingGoal(null)} onConfirm={contribute} />

      <AdjustBalanceDialog goal={adjustingGoal} onClose={() => setAdjustingGoal(null)} onConfirm={adjustBalance} />

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={5000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='success' onClose={() => setMessage(null)}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );

  return {
    goals,
    activeGoals,
    archivedGoals,
    trails,
    isLoading,
    error,
    clearError: () => setError(null),
    now,
    inGroup,
    canEdit,
    canModify,
    canUndo,
    openCreate,
    openContribute: (goal: Goal) => setContributingGoal(goal),
    openAdjust: (goal: Goal) => setAdjustingGoal(goal),
    openMenu: (element: HTMLElement, goal: Goal) => setMenuAnchor({ element, goal }),
    unarchiveGoal,
    removeContribution,
    reorder,
    dialogs,
  };
};
